/**
 * Boss template for configurator
 */

import { ConfiguratorConfig } from '../types';
import translationInstance from '../utils/translations';
import PartialUpholstery from './partialUpholstery';

/**
 * Deep clone utility function
 */
const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Maps actions from prefix-based to specific object names for Boss model
 */
const mapActionsForBossSection = (
  actions: any[],
  objectNames: string[]
): any[] => {
  return actions
    .filter(action => action.functionName === 'setMaterialByObjectPrefixMaterialName')
    .flatMap(action => 
      objectNames.map((objectName, index) => ({
        _id: `${action._id}-${index}`,
        functionName: 'setMaterialByObjectNameMaterialName',
        name: objectName,
        materialName: action.materialName
      }))
    )
    .concat(
      actions.filter(action => action.functionName !== 'setMaterialByObjectPrefixMaterialName')
    );
};

/**
 * Recursively processes subOptions to map actions and update section headlines
 */
const processSubOptions = (
  subOptions: any[], 
  objectNames: string[], 
  sectionHeadlineText?: string
): any[] => {
  return subOptions.map((subOption, index) => {
    // Update the first section headline with the specific section name
    if (index === 0 && subOption.controlType === 'sectionHeadline' && sectionHeadlineText) {
      return {
        ...subOption,
        name: sectionHeadlineText
      };
    }
    
    if (subOption.controlType === 'section' && subOption.subOptions) {
      return {
        ...subOption,
        subOptions: processSubOptions(subOption.subOptions, objectNames)
      };
    }
    
    if (subOption.controlType === 'imageButton' && subOption.actions) {
      return {
        ...subOption,
        actions: mapActionsForBossSection(subOption.actions, objectNames)
      };
    }
    
    return subOption;
  });
};

/**
 * Clones and adapts partial upholstery options for a specific Boss section
 */
const createBossSectionFromPartial = (
  sectionName: string,
  headlineText: string,
  objectNames: string[],
  classes: string[] = []
): any => {
  const clonedSection: any = deepClone(PartialUpholstery);
  
  clonedSection._id = `boss-${sectionName}-section`;
  clonedSection.name = sectionName;
  clonedSection.headlineText = headlineText;
  clonedSection.classes = ['virtenzo-section-surface', ...classes];
  clonedSection.controlType = 'section';
  clonedSection.href = '';
  clonedSection.actions = [];
  
  clonedSection.subOptions = processSubOptions(clonedSection.subOptions, objectNames, headlineText);
  
  return clonedSection;
};

export const createBossTemplate = (modelId: string): ConfiguratorConfig => {
  const t = translationInstance.t.bind(translationInstance);

  const backrestSection = createBossSectionFromPartial(
    'backrest',
    t('virtenzo.configurator.section_upholstery_backrest'),
    ['fabric_5_Bel Sand_0', 'fabric_4_Bel Sand_0']
  );

  const seatSection = createBossSectionFromPartial(
    'seat',
    t('virtenzo.configurator.section_upholstery_seat'),
    ['fabric_3_Bel Sand_0', 'fabric_2_Bel Sand_0', 'fabric_1_Bel Sand_0']
  );

  return {
    viewer: {
      modelId: modelId,
      background: {
        color: [
          0.91015625,
          0.91015625,
          0.91015625
        ]
      },
      defaultActions: []
    },
    configurator: {
      options: [
        backrestSection,
        seatSection
      ],
      queryConfig: true,
      selector: "virtenzo-panel"
    }
  }
}